import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Plus, Trash2, Save, LogOut, ExternalLink, Upload, BarChart3, GripVertical, ChevronUp, ChevronDown } from "lucide-react";
import { toast, Toaster } from "sonner";
import RecepPetLogo from "@/components/RecepPetLogo";
import { resolveUrl } from "@/lib/media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "receppet_admin_token";
const MAX_PARTNERS = 30;

const errText = (d) => {
  if (!d) return "Erro inesperado. Tente novamente.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d.msg || d);
};

const inputCls =
  "w-full rounded-lg border border-[#0B3C5D]/20 bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-[#00A86B] focus:ring-2 focus:ring-[#00A86B]/20";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#0B3C5D]/60">{label}</span>
    {children}
  </label>
);

const ImageUploader = ({ value, onChange, token, testId, label = "Enviar imagem" }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange(data.url);
      toast.success("Imagem enviada!");
    } catch (e2) {
      toast.error(errText(e2.response?.data?.detail));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-3">
      {value ? (
        <img
          src={resolveUrl(value)}
          alt=""
          className="h-12 w-12 rounded-lg border border-[#0B3C5D]/15 object-cover"
          data-testid={`${testId}-preview`}
        />
      ) : null}
      <input ref={ref} type="file" accept="image/*" onChange={pick} className="hidden" data-testid={`${testId}-file-input`} />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={busy}
        data-testid={`${testId}-upload-button`}
        className="inline-flex items-center gap-1.5 rounded-full border border-[#00A86B] px-4 py-2 text-sm font-bold text-[#00A86B] transition-colors hover:bg-[#00A86B]/10 disabled:opacity-60"
      >
        <Upload className="h-4 w-4" /> {busy ? "Enviando..." : label}
      </button>
      {value ? (
        <button
          type="button"
          onClick={() => onChange("")}
          data-testid={`${testId}-clear-button`}
          className="text-xs font-semibold text-red-600 hover:underline"
        >
          Remover
        </button>
      ) : null}
    </div>
  );
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data.user.role !== "admin") throw new Error("Acesso restrito a administradores");
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin(data.access_token);
    } catch (e2) {
      setError(errText(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#002B49] px-5" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-2xl">
        <RecepPetLogo className="h-10" />
        <h1 className="mt-6 text-2xl font-extrabold text-[#002B49]">Painel administrativo</h1>
        <div className="mt-6 space-y-4">
          <Field label="E-mail">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
              data-testid="admin-login-email"
              required
            />
          </Field>
          <Field label="Senha">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
              data-testid="admin-login-password"
              required
            />
          </Field>
        </div>
        {error && (
          <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-6 w-full rounded-full bg-[#00A86B] py-3 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {loading ? "Entrando..." : "Entrar"}
        </button>
      </form>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [config, setConfig] = useState(null);
  const [clicks, setClicks] = useState(null);
  const [saving, setSaving] = useState(false);
  const dragIndex = useRef(null);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/landing-page`)
      .then(({ data }) =>
        setConfig({
          ...data,
          benefits: data.benefits || [],
          featuredPartners: data.featuredPartners || [],
          profileCards: data.profileCards || [],
        })
      )
      .catch(() => toast.error("Não foi possível carregar a configuração"));
    axios
      .get(`${API}/admin/cta-clicks`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setClicks(data))
      .catch(() => {});
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setConfig(null);
  };

  const set = (key, value) => setConfig((c) => ({ ...c, [key]: value }));
  const setBenefit = (i, key, value) =>
    setConfig((c) => ({ ...c, benefits: c.benefits.map((b, idx) => (idx === i ? { ...b, [key]: value } : b)) }));
  const setProfileCard = (i, key, value) =>
    setConfig((c) => ({ ...c, profileCards: c.profileCards.map((p, idx) => (idx === i ? { ...p, [key]: value } : p)) }));
  const setPartner = (i, key, value) =>
    setConfig((c) => ({
      ...c,
      featuredPartners: c.featuredPartners.map((p, idx) => (idx === i ? { ...p, [key]: value } : p)),
    }));

  const addPartner = () => {
    if (config.featuredPartners.length >= MAX_PARTNERS) {
      toast.error(`Limite de ${MAX_PARTNERS} parceiros atingido`);
      return;
    }
    set("featuredPartners", [
      ...config.featuredPartners,
      { name: "", category: "", location: "", imageUrl: "", imageFit: "contain" },
    ]);
  };

  const movePartner = (from, to) => {
    setConfig((c) => {
      if (to < 0 || to >= c.featuredPartners.length || from === to) return c;
      const list = [...c.featuredPartners];
      const [item] = list.splice(from, 1);
      list.splice(to, 0, item);
      return { ...c, featuredPartners: list };
    });
  };

  const save = async () => {
    if (config.featuredPartners.length > MAX_PARTNERS) {
      toast.error(`Máximo de ${MAX_PARTNERS} parceiros em destaque`);
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/landing-page`,
        {
          logoUrl: config.logoUrl || "",
          heroBadge: config.heroBadge || "",
          heroTitle: config.heroTitle || "",
          heroSubtitle: config.heroSubtitle || "",
          partnershipHighlight: config.partnershipHighlight || "",
          videoUrl: config.videoUrl || "",
          heroBannerUrl: config.heroBannerUrl || "",
          ctaButtonText: config.ctaButtonText || "",
          ctaButtonColor: config.ctaButtonColor || "#00A86B",
          benefits: config.benefits.map((b) => ({
            title: b.title || "",
            description: b.description || "",
            iconUrl: b.iconUrl || "",
          })),
          profileCards: config.profileCards.map((c) => ({
            title: c.title || "",
            description: c.description || "",
          })),
          featuredPartners: config.featuredPartners.map((p) => ({
            name: p.name || "",
            category: p.category || "",
            location: p.location || "",
            imageUrl: p.imageUrl || "",
            imageFit: p.imageFit === "cover" ? "cover" : "contain",
          })),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConfig({
        ...data,
        benefits: data.benefits || [],
        featuredPartners: data.featuredPartners || [],
        profileCards: data.profileCards || [],
      });
      toast.success("Vitrine atualizada com sucesso!");
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Sessão expirada. Faça login novamente.");
        logout();
      } else {
        toast.error(errText(e.response?.data?.detail));
      }
    } finally {
      setSaving(false);
    }
  };

  if (!token) return <Login onLogin={setToken} />;
  if (!config) return <div className="p-10 text-sm text-[#0B3C5D]">Carregando...</div>;

  const partnerCount = config.featuredPartners.length;  const limitReached = partnerCount >= MAX_PARTNERS;
  const maxClick = Math.max(1, ...((clicks?.daily || []).map((d) => d.count || 0)));

  return (
    <div className="min-h-screen bg-[#F4F7F6] pb-24" data-testid="admin-page">
      <Toaster position="top-center" richColors />
      <header className="border-b border-[#0B3C5D]/10 bg-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-5 py-4">
          <RecepPetLogo className="h-9" />
          <div className="flex items-center gap-3">
            <a
              href="/parceiros"
              target="_blank"
              rel="noreferrer"
              data-testid="admin-view-landing-link"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#00A86B] hover:underline"
            >
              Ver /parceiros <ExternalLink className="h-4 w-4" />
            </a>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#0B3C5D]/20 px-4 py-2 text-sm font-semibold text-[#0B3C5D] hover:bg-[#0B3C5D]/5"
            >
              <LogOut className="h-4 w-4" /> Sair
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-5 py-8">
        <div className="mb-8 inline-flex rounded-full bg-white p-1 shadow-sm">
          <span
            data-testid="admin-tab-vitrine"
            className="rounded-full bg-[#002B49] px-5 py-2 text-sm font-bold text-white"
          >
            Gerenciar Vitrine (/parceiros)
          </span>
        </div>

        {/* CLICKS */}
        <section className="mb-6 rounded-2xl bg-white p-6 shadow-sm" data-testid="admin-section-clicks">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-[#00A86B]" />
            <h2 className="text-base font-bold text-[#002B49] md:text-lg">Cliques no botão de cadastro</h2>
          </div>
          <div className="mt-4 flex flex-wrap gap-8">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[#0B3C5D]/55">Hoje</p>
              <p className="text-3xl font-extrabold text-[#002B49]" data-testid="clicks-today">
                {clicks?.today ?? 0}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[#0B3C5D]/55">Total (30 dias)</p>
              <p className="text-3xl font-extrabold text-[#002B49]" data-testid="clicks-total">
                {clicks?.total ?? 0}
              </p>
            </div>
          </div>
          <div className="mt-5 space-y-2" data-testid="clicks-daily-list">
            {(clicks?.daily || []).length === 0 && (
              <p className="text-sm text-[#0B3C5D]/60">Nenhum clique registrado ainda.</p>
            )}
            {(clicks?.daily || []).map((d) => (
              <div key={d.day} className="flex items-center gap-3" data-testid={`clicks-row-${d.day}`}>
                <span className="w-24 shrink-0 text-xs font-semibold text-[#0B3C5D]/70">
                  {d.day.split("-").reverse().join("/")}
                </span>
                <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-[#0B3C5D]/10">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#00B074] to-[#0B3C5D]"
                    style={{ width: `${Math.round(((d.count || 0) / maxClick) * 100)}%` }}
                  />
                </div>
                <span className="w-10 text-right text-sm font-bold text-[#002B49]">{d.count || 0}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-2xl bg-white p-6 shadow-sm" data-testid="admin-section-general">
          <h2 className="text-base font-bold text-[#002B49] md:text-lg">Identidade e Hero</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <Field label="Logotipo">
              <div className="space-y-3">
                <ImageUploader
                  value={config.logoUrl}
                  onChange={(v) => set("logoUrl", v)}
                  token={token}
                  testId="admin-logo"
                  label="Enviar logotipo"
                />
                <input
                  value={config.logoUrl || ""}
                  onChange={(e) => set("logoUrl", e.target.value)}
                  className={inputCls}
                  placeholder="ou cole uma URL"
                  data-testid="admin-input-logoUrl"
                />
              </div>
            </Field>
            <Field label="Banner do hero">
              <div className="space-y-3">
                <ImageUploader
                  value={config.heroBannerUrl}
                  onChange={(v) => set("heroBannerUrl", v)}
                  token={token}
                  testId="admin-banner"
                  label="Enviar banner"
                />
                <input
                  value={config.heroBannerUrl || ""}
                  onChange={(e) => set("heroBannerUrl", e.target.value)}
                  className={inputCls}
                  placeholder="ou cole uma URL"
                  data-testid="admin-input-heroBannerUrl"
                />
              </div>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Badge do hero">
                <input
                  value={config.heroBadge || ""}
                  onChange={(e) => set("heroBadge", e.target.value)}
                  className={inputCls}
                  data-testid="admin-input-heroBadge"
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Título principal">
                <input
                  value={config.heroTitle || ""}
                  onChange={(e) => set("heroTitle", e.target.value)}
                  className={inputCls}
                  data-testid="admin-input-heroTitle"
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Subtítulo">
                <textarea
                  rows={3}
                  value={config.heroSubtitle || ""}
                  onChange={(e) => set("heroSubtitle", e.target.value)}
                  className={inputCls}
                  data-testid="admin-input-heroSubtitle"
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Frase de destaque da parceria (card abaixo do hero)">
                <textarea
                  rows={3}
                  value={config.partnershipHighlight || ""}
                  onChange={(e) => set("partnershipHighlight", e.target.value)}
                  className={inputCls}
                  data-testid="admin-input-partnershipHighlight"
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="URL do vídeo (YouTube ou MP4)">
                <input
                  value={config.videoUrl || ""}
                  onChange={(e) => set("videoUrl", e.target.value)}
                  className={inputCls}
                  data-testid="admin-input-videoUrl"
                />
              </Field>
            </div>
            <Field label="Texto do botão CTA">
              <input
                value={config.ctaButtonText || ""}
                onChange={(e) => set("ctaButtonText", e.target.value)}
                className={inputCls}
                data-testid="admin-input-ctaButtonText"
              />
            </Field>
            <Field label="Cor do botão CTA">
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={config.ctaButtonColor || "#00A86B"}
                  onChange={(e) => set("ctaButtonColor", e.target.value)}
                  className="h-11 w-14 cursor-pointer rounded-lg border border-[#0B3C5D]/20 bg-white"
                  data-testid="admin-input-ctaButtonColorPicker"
                />
                <input
                  value={config.ctaButtonColor || ""}
                  onChange={(e) => set("ctaButtonColor", e.target.value)}
                  className={inputCls}
                  data-testid="admin-input-ctaButtonColor"
                />
              </div>
            </Field>
          </div>
        </section>

        <section className="mt-6 rounded-2xl bg-white p-6 shadow-sm" data-testid="admin-section-profile-cards">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-[#002B49] md:text-lg">Quem pode ser nosso parceiro</h2>
            <button
              onClick={() => set("profileCards", [...config.profileCards, { title: "", description: "" }])}
              data-testid="admin-add-profile-card-button"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#00A86B] px-4 py-2 text-sm font-bold text-white hover:opacity-90"
            >
              <Plus className="h-4 w-4" /> Adicionar
            </button>
          </div>
          <div className="mt-5 space-y-4">
            {config.profileCards.map((c, i) => (
              <div key={i} className="rounded-xl border border-[#0B3C5D]/10 p-4" data-testid={`admin-profile-card-row-${i}`}>
                <Field label={`Título do perfil ${i + 1}`}>
                  <input
                    value={c.title || ""}
                    onChange={(e) => setProfileCard(i, "title", e.target.value)}
                    className={inputCls}
                    data-testid={`admin-profile-card-title-${i}`}
                  />
                </Field>
                <div className="mt-4">
                  <Field label="Descrição">
                    <textarea
                      rows={2}
                      value={c.description || ""}
                      onChange={(e) => setProfileCard(i, "description", e.target.value)}
                      className={inputCls}
                      data-testid={`admin-profile-card-description-${i}`}
                    />
                  </Field>
                </div>
                <button
                  onClick={() => set("profileCards", config.profileCards.filter((_, idx) => idx !== i))}
                  data-testid={`admin-profile-card-remove-${i}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:underline"
                >
                  <Trash2 className="h-4 w-4" /> Remover
                </button>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-6 rounded-2xl bg-white p-6 shadow-sm" data-testid="admin-section-benefits">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-[#002B49] md:text-lg">Benefícios</h2>
            <button
              onClick={() => set("benefits", [...config.benefits, { title: "", description: "", iconUrl: "" }])}
              data-testid="admin-add-benefit-button"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#00A86B] px-4 py-2 text-sm font-bold text-white hover:opacity-90"
            >
              <Plus className="h-4 w-4" /> Adicionar
            </button>
          </div>
          <div className="mt-5 space-y-4">
            {config.benefits.map((b, i) => (
              <div key={i} className="rounded-xl border border-[#0B3C5D]/10 p-4" data-testid={`admin-benefit-row-${i}`}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={`Título ${i + 1}`}>
                    <input
                      value={b.title || ""}
                      onChange={(e) => setBenefit(i, "title", e.target.value)}
                      className={inputCls}
                      data-testid={`admin-benefit-title-${i}`}
                    />
                  </Field>
                  <Field label="Ícone (upload ou URL)">
                    <div className="space-y-3">
                      <ImageUploader
                        value={b.iconUrl}
                        onChange={(v) => setBenefit(i, "iconUrl", v)}
                        token={token}
                        testId={`admin-benefit-icon-upload-${i}`}
                        label="Enviar ícone"
                      />
                      <input
                        value={b.iconUrl || ""}
                        onChange={(e) => setBenefit(i, "iconUrl", e.target.value)}
                        className={inputCls}
                        data-testid={`admin-benefit-icon-${i}`}
                      />
                    </div>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Descrição">
                      <textarea
                        rows={2}
                        value={b.description || ""}
                        onChange={(e) => setBenefit(i, "description", e.target.value)}
                        className={inputCls}
                        data-testid={`admin-benefit-description-${i}`}
                      />
                    </Field>
                  </div>
                </div>
                <button
                  onClick={() => set("benefits", config.benefits.filter((_, idx) => idx !== i))}
                  data-testid={`admin-benefit-remove-${i}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:underline"
                >
                  <Trash2 className="h-4 w-4" /> Remover
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* FEATURED PARTNERS */}
        <section className="mt-6 rounded-2xl bg-white p-6 shadow-sm" data-testid="admin-section-partners">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-bold text-[#002B49] md:text-lg">Já fazem parte do nosso time</h2>
              <p
                className={`mt-1 text-sm font-semibold ${limitReached ? "text-red-600" : "text-[#0B3C5D]/60"}`}
                data-testid="partners-counter"
              >
                {partnerCount} / {MAX_PARTNERS} parceiros cadastrados
              </p>
            </div>
            <button
              onClick={addPartner}
              disabled={limitReached}
              data-testid="admin-add-partner-button"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#00A86B] px-4 py-2 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Plus className="h-4 w-4" /> Adicionar parceiro
            </button>
          </div>
          {limitReached && (
            <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="partners-limit-warning">
              Limite de {MAX_PARTNERS} parceiros atingido. Remova um parceiro para cadastrar outro.
            </p>
          )}
          <div className="mt-5 space-y-4">
            {config.featuredPartners.map((p, i) => (
              <div
                key={i}
                draggable
                onDragStart={() => (dragIndex.current = i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragIndex.current !== null) movePartner(dragIndex.current, i);
                  dragIndex.current = null;
                }}
                className="rounded-xl border border-[#0B3C5D]/10 p-4 transition-colors hover:border-[#00A86B]/40"
                data-testid={`admin-partner-row-${i}`}
              >
                <div className="mb-3 flex items-center justify-between">
                  <span className="inline-flex cursor-grab items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#0B3C5D]/50">
                    <GripVertical className="h-4 w-4" /> Posição {i + 1} · arraste para reordenar
                  </span>
                  <span className="flex gap-1.5">
                    <button
                      onClick={() => movePartner(i, i - 1)}
                      disabled={i === 0}
                      data-testid={`admin-partner-move-up-${i}`}
                      className="rounded-lg border border-[#0B3C5D]/20 p-1.5 text-[#0B3C5D] hover:bg-[#0B3C5D]/5 disabled:opacity-30"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => movePartner(i, i + 1)}
                      disabled={i === partnerCount - 1}
                      data-testid={`admin-partner-move-down-${i}`}
                      className="rounded-lg border border-[#0B3C5D]/20 p-1.5 text-[#0B3C5D] hover:bg-[#0B3C5D]/5 disabled:opacity-30"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </span>
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label={`Nome ${i + 1}`}>
                    <input
                      value={p.name || ""}
                      onChange={(e) => setPartner(i, "name", e.target.value)}
                      className={inputCls}
                      data-testid={`admin-partner-name-${i}`}
                    />
                  </Field>
                  <Field label="Categoria">
                    <input
                      value={p.category || ""}
                      onChange={(e) => setPartner(i, "category", e.target.value)}
                      className={inputCls}
                      placeholder="Pet Shop, Veterinária..."
                      data-testid={`admin-partner-category-${i}`}
                    />
                  </Field>
                  <Field label="Localização">
                    <input
                      value={p.location || ""}
                      onChange={(e) => setPartner(i, "location", e.target.value)}
                      className={inputCls}
                      placeholder="Petrópolis / RJ"
                      data-testid={`admin-partner-location-${i}`}
                    />
                  </Field>
                  <div className="sm:col-span-3">
                    <Field label="Foto / logotipo">
                      <div className="space-y-3">
                        <ImageUploader
                          value={p.imageUrl}
                          onChange={(v) => setPartner(i, "imageUrl", v)}
                          token={token}
                          testId={`admin-partner-image-${i}`}
                          label="Enviar foto"
                        />
                        <input
                          value={p.imageUrl || ""}
                          onChange={(e) => setPartner(i, "imageUrl", e.target.value)}
                          className={inputCls}
                          placeholder="ou cole uma URL"
                          data-testid={`admin-partner-imageUrl-${i}`}
                        />
                      </div>
                    </Field>
                  </div>
                  <div className="sm:col-span-3">
                    <Field label="Encaixe da imagem no círculo">
                      <select
                        value={p.imageFit === "cover" ? "cover" : "contain"}
                        onChange={(e) => setPartner(i, "imageFit", e.target.value)}
                        className={inputCls}
                        data-testid={`admin-partner-imageFit-${i}`}
                      >
                        <option value="contain">Logotipo — mostrar inteira, sem cortar (recomendado)</option>
                        <option value="cover">Foto — preencher o círculo (pode cortar as bordas)</option>
                      </select>
                    </Field>
                  </div>
                </div>
                <button
                  onClick={() => set("featuredPartners", config.featuredPartners.filter((_, idx) => idx !== i))}
                  data-testid={`admin-partner-remove-${i}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:underline"
                >
                  <Trash2 className="h-4 w-4" /> Remover parceiro
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 border-t border-[#0B3C5D]/10 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4">
          <span className="text-xs font-semibold text-[#0B3C5D]/60" data-testid="footer-partners-counter">
            {partnerCount} / {MAX_PARTNERS} parceiros
          </span>
          <button
            onClick={save}
            disabled={saving}
            data-testid="admin-save-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#002B49] px-8 py-3 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            <Save className="h-4 w-4" /> {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}
